import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const guestsQuestions = [
  {
    category: "general",
    questions: [
      {
        key: "location",
        title: msg`Where is El Safwa Resort located?`,
        content: msg`El Safwa offers three prime locations across New Cairo's Fifth Settlement: El Safwa Resort, Apartments in Katameya Dunes Compound, and Apartments in CFC Compound. All three locations provide excellent connectivity to Cairo International Airport, Cairo Festival City Mall, Downtown Katameya, and the city's major business districts, while offering the same high standards of comfort, privacy, and hospitality.`,
      },
      {
        key: "accommodation-types",
        title: msg`What types of accommodation do you offer?`,
        content: msg`We offer fully furnished serviced apartments and villas, including: Single Rooms, One-Bedroom Apartments, Two-Bedroom Apartments, Three-Bedroom Apartments, Three-Bedroom Duplexes, and Five-Bedroom Villas.`,
      },
      {
        key: "furnished",
        title: msg`Are the apartments fully furnished?`,
        content: msg`Yes. All units are fully furnished and equipped with a kitchen, appliances, air conditioning, high-speed Wi-Fi, housekeeping services, and everything needed for both short- and long-term stays.`,
      },
      {
        key: "stay-duration",
        title: msg`Do you accept short-term and long-term stays?`,
        content: msg`Yes. We welcome guests for both short-term and long-term stays, whether for tourism, business assignments, relocation, or family accommodation.`,
      },
    ],
  },
  {
    category: "reservations",
    questions: [
      {
        key: "security-deposit",
        title: msg`Is there a refundable security deposit?`,
        content: msg`Yes. A refundable security deposit is required upon check-in and will be refunded after check-out, provided that no damages or extra charges are incurred during the stay.`,
      },
      {
        key: "payment-methods",
        title: msg`Which payment methods do you accept?`,
        content: msg`We accept: Credit & Debit Cards, Bank Transfers, Cash Payments, and Corporate Payments (subject to agreement).`,
      },
      {
        key: "corporate-rates",
        title: msg`Do you offer corporate rates?`,
        content: msg`Yes. We offer special corporate rates for companies, embassies, international organizations, and relocation agencies based on the length of stay and accommodation requirements.`,
      },
    ],
  },
  {
    category: "residences",
    questions: [
      {
        key: "housekeeping",
        title: msg`Is housekeeping included?`,
        content: msg`Yes. Housekeeping services are provided according to the reservation terms to ensure a comfortable and enjoyable stay.`,
      },
      {
        key: "pets",
        title: msg`Can I bring my pet?`,
        content: msg`Pet policies vary by property: El Safwa Resort — pets are not permitted. Apartments in Katameya Dunes Compound — well-behaved pets are welcome. Apartments in CFC Compound — well-behaved pets are welcome. Guests staying at Katameya Dunes or CFC Compound are kindly requested to keep pets on a leash in common areas and ensure they do not disturb other guests or cause any damage to the property.`,
      },
    ],
  },
  {
    category: "facilities",
    questions: [
      {
        key: "resort-facilities",
        title: msg`What facilities are available at El Safwa Resort?`,
        content: msg`Guests staying at El Safwa Resort can enjoy: 3,300 m² Swimming Pool, Kids' Swimming Pools, Gym, Spa, Jacuzzi, Restaurant, Children's Play Area, 24/7 Security, Maintenance Services, Housekeeping, Parking, and High-Speed Wi-Fi.`,
      },
      {
        key: "katameya-facilities",
        title: msg`What facilities are available at El Safwa Katameya Dunes?`,
        content: msg`Guests staying at El Safwa Katameya Dunes can enjoy: Garden & Barbecue Area, Swimming Pool, 24/7 Security, Maintenance Services, and Housekeeping. In addition, guests have complimentary access to all facilities at El Safwa Resort during their stay.`,
      },
    ],
  },
  {
    category: "location",
    questions: [
      {
        key: "airport-transfers",
        title: msg`Do you provide airport transfers?`,
        content: msg`Yes. Airport transfers can be arranged upon request for an additional charge.`,
      },
    ],
  },
  {
    category: "promotions",
    questions: [
      {
        key: "corporate-program",
        title: msg`Do you offer corporate rates?`,
        content: msg`Yes, we provide special corporate rates for business travelers and companies with frequent bookings. Our corporate program includes discounted rates, flexible cancellation policies, and dedicated account management. Contact our corporate sales team to set up an account and receive your company's special pricing.`,
      },
    ],
  },
];

const ownersQuestions = [
  {
    category: "general",
    questions: [
      {
        key: "liability-coverage",
        title: msg`What liability coverage do I have by leasing my property to you?`,
        content: msg`When you lease your property to us, you're protected under our standard liability framework. This includes coverage for damages caused by verified guests during their stay, subject to our terms and conditions. While we take all necessary steps to screen guests and minimize risk.`,
      },
      {
        key: "properties",
        title: msg`What types of properties do you accept?`,
        content: msg`We accept a wide variety of residential properties including apartments, townhouses, and luxury villas. Properties should be in good condition, well-maintained, and located in desirable areas with good accessibility to amenities, transportation, and attractions. We also consider unique properties like penthouses, lofts, and waterfront homes that offer distinctive guest experiences.`,
      },
      {
        key: "lease-duration",
        title: msg`What is the lease duration?`,
        content: msg`We offer flexible lease terms to accommodate different property owner needs. Our standard lease duration is 24 months or more. For seasonal properties or vacation rentals, we can arrange shorter-term agreements.`,
      },
      {
        key: "services",
        title: msg`What are the services do you offer?`,
        content: msg`We provide comprehensive property management services including 24/7 guest support, cleaning and maintenance coordination, and revenue optimization. Our team handles everything from listing creation to guest communication, ensuring your property generates maximum returns while you enjoy a hands-off experience.`,
      },
    ],
  },
  {
    category: "safety",
    questions: [
      {
        key: "property-protection",
        title: msg`How do you protect my property from damage?`,
        content: msg`We implement multiple layers of property protection including comprehensive guest screening, security deposits, and 24/7 monitoring systems. All guests are verified with valid identification and payment methods, and we maintain strict house rules. Our cleaning teams conduct thorough inspections between stays, and we have immediate response protocols for any incidents.`,
      },
      {
        key: "guest-verification",
        title: msg`What guest verification measures do you use?`,
        content: msg`We use advanced verification systems including identity verification, background checks, and previous stay reviews. All guests must provide government-issued ID, verified payment methods, and agree to our terms of service. We maintain a comprehensive guest database and have a zero-tolerance policy for problematic guests.`,
      },
    ],
  },
  {
    category: "management",
    questions: [
      {
        key: "booking-management",
        title: msg`How do you manage bookings?`,
        content: msg`We handle all aspects of booking management including pricing optimization, availability management, and guest communication. Our dynamic pricing system adjusts rates based on demand, seasonality, and local events to maximize your revenue. We also coordinate check-ins, check-outs, and any special guest requests to ensure smooth operations.`,
      },
      {
        key: "maintenance-coordination",
        title: msg`How do you coordinate property maintenance?`,
        content: msg`We coordinate all routine maintenance and work with trusted local contractors for repairs. Our team conducts regular property inspections and coordinates cleaning between guests. We handle minor repairs and coordinate with you for major structural work. All maintenance is documented and you receive detailed reports of any work performed.`,
      },
    ],
  },
  {
    category: "pricing",
    questions: [
      {
        key: "revenue-optimization",
        title: msg`How do you optimize my revenue?`,
        content: msg`Our revenue optimization includes dynamic pricing strategies, seasonal rate adjustments, and demand forecasting. We analyze market trends, competitor pricing, and local events to set optimal rates. We also offer add-on services like early check-in and late check-out.`,
      },
    ],
  },
  {
    category: "policies",
    questions: [
      {
        key: "cancellation-policy",
        title: msg`What is your cancellation policy for guests?`,
        content: msg`We offer flexible cancellation policies to maximize bookings while protecting your interests. Our standard policy allows full refunds up to 48 hours before check-in, with partial refunds for shorter notice periods. For peak seasons, we may implement stricter policies. All cancellations are immediately communicated and we work quickly to rebook dates.`,
      },
    ],
  },
];

const Questions = ({ type, category }: { type: string; category: string }) => {
  const data = type === "owners" ? ownersQuestions : guestsQuestions;
  const selectedCategory = data.find((c) => c.category === category);
  const { i18n } = useLingui();

  if (!selectedCategory?.questions.length) {
    return (
      <p className="text-base font-medium">
        <Trans>No questions available.</Trans>
      </p>
    );
  }

  return (
    <Accordion
      type="single"
      key={category}
      className="flex flex-col justify-between"
      defaultValue={selectedCategory.questions[0]?.key}
    >
      {selectedCategory.questions.map((que) => (
        <AccordionItem key={que.key} value={que.key}>
          <AccordionTrigger className="text-xl leading-7 font-semibold hover:no-underline">
            {i18n._(que.title)}
          </AccordionTrigger>
          <AccordionContent className="text-base leading-[26px] text-wrap">
            {i18n._(que.content)}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default Questions;
